import type { Layer } from '@trimble/web3d-providers';
import type { LayerCreateParams } from '../layer-create-popup/LayerCreatePopup';

export interface SurfaceItem {
  readonly id: string;
  readonly guid: string;
  readonly name: string;
  readonly color: string;
  readonly visible: boolean;
  readonly layerId?: string;
}

export interface GenericLayerItemSource {
  readonly id: string;
}

export type LayerItemSource = Layer | SurfaceItem | GenericLayerItemSource;

export interface LayerItemObject {
  readonly name: string;
  readonly editable: boolean;
  readonly color: string;
  readonly lineType?: string;
  readonly lineWeight?: number;
  readonly object: LayerItemSource;
  readonly guid: string;
  readonly visible: boolean;
  readonly id: string;
  readonly isSurface: boolean;
  readonly isIsolated: boolean;
  readonly isDefault: boolean;
  readonly category?: LayerCategoryKey;
  /**
   * Secondary text rendered after {@link name} in a smaller font, e.g. the
   * source file and page number of a PDF page. The tooltip shows name and
   * suffix together at a uniform size.
   */
  readonly nameSuffix?: string;
  /** Defaults to true; PDF pages have no colour to show. */
  readonly showColorIndicator?: boolean;
  readonly visibilityToggleDisabled?: boolean;
  readonly visibilityToggleDisabledReason?: string;
  readonly showContextMenu?: boolean;
  /** Geometry-defined data-prep surfaces that support the surface editor (including empty definitions). */
  readonly supportsSurfaceEditor?: boolean;
}

export enum LayerScopeDropdownOptions {
  All = 'all_layers',
  Isolated = 'isolated_layers',
}

/** Stable key for layer category sections in tests and analytics. */
export type LayerCategoryKey = 'cad' | 'surface' | 'feature' | 'pdfPage';

export interface LayerListPanelProps {
  readonly isViewOnly?: boolean;
  readonly designId: string | undefined;
  readonly items: LayerItemObject[];
  readonly loadingIds?: string[];
  readonly hideLayerViewAction?: boolean;
  /** Defaults to the feature category. */
  readonly initiallyCollapsedCategoryKeys?: readonly LayerCategoryKey[];
  handleLayerCreate?(item: LayerCreateParams): void;
  handleToggledItemVisibility(
    item: LayerItemObject[],
    visibility: boolean
  ): void;
  handleSelectedItems?(items: LayerItemObject[]): void;
  handleDeleteItem?(item: LayerItemObject): void;
  handleLayerNameChange?(layerIds: number[], newName: string): void;
  handlePdfPageNameChange?(pdfFilePageGuid: string, newName: string): void;
  handleLayerColorChange?(layerIds: number[], color: string): void;
  handleLayerDelete?(layerId: string[]): void;
  handleLayerLineStyleChange?(layerIds: number[], lineStyle: string): void;
  handleLayerLineThicknessChange?(
    layerIds: number[],
    lineThickness: number
  ): void;
  handleLayerIsolationChange?(
    items: LayerItemObject[],
    isIsolated: boolean
  ): void;
  handleLayerScopeChange?(key: LayerScopeDropdownOptions): void;
  handleToggleAllLayersVisibility?(visible: boolean): void;
  handleSurfaceEdit?(item: LayerItemObject): void;
  handleSurfaceExportToProjectData?(item: LayerItemObject): void;
  handleSurfaceDownload?(item: LayerItemObject): void;
  handleActiveLayerChange?(layerIds: LayerItemObject[]): void;
  handleFitToView?(
    editable: boolean,
    layerId?: number,
    surfaceGuid?: string
  ): void;
}
